<template>
  <div class="row q-col-gutter-x-lg">
    <div class="col-12 col-md-8">
      <q-card bordered flat class="card-container">
        <q-card-section class="bg-primary text-white">
          <div>
            <div class="text-bold" style="font-size: 1.2rem">
              <template v-if="addOn"> Activate {{ planName }} </template>
              <template v-else> Start your 7-day free trial today </template>
            </div>
            <div v-if="!addOn" class="text-grey-1">
              <q-icon name="credit_card" class="q-mr-xs" /> Enter payment details
            </div>
          </div>
        </q-card-section>
        <q-card-section class="q-pa-lg">
          <template v-if="!addOn">
            <div style="font-size: 0.93rem; margin-bottom: 4px">Card Information</div>
            <div
              id="card-element"
              ref="card-element"
              :class="{ 'bg-grey-10': $q.dark.isActive }"
            ></div>
            <div id="card-errors" class="text-red text-caption q-pt-sm" role="alert"></div>
            <div id="address-element" ref="address-element" class="q-pt-sm" />

            <div class="text-grey-7 q-mt-md">
              <q-icon name="lock_outline" class="q-mr-xs" />
              Your payment information is processed securely. We do not store your credit card
              details.
            </div>
            <q-input
              v-model="promoCode"
              :error="promoCodeError"
              no-error-icon
              error-message="This code is invalid."
              filled
              placeholder="Add promotion code"
              class="q-my-md"
            />
          </template>
          <template v-else>
            <div class="q-mb-md">
              Power your market surveys with
              <b>real-time data on over 35M multifamily units</b> nationwide, sourced entirely from
              property websites and public data sources.
            </div>
          </template>
          <div v-if="isPrimaryOwner" class="row q-py-xs q-my-md">
            <q-checkbox dense v-model="agreeToTerms" class="q-mr-sm" />
            <div class="q-my-auto">
              I agree to HelloData's
              <a target="_blank" class="text-primary" href="https://www.hellodata.ai/terms/"
                >Terms of Service</a
              >
              and
              <a
                target="_blank"
                class="text-primary"
                href="https://www.hellodata.ai/privacy-policy/"
              >
                Privacy Policy </a
              >.
            </div>
          </div>
          <div v-if="isPrimaryOwner">
            <q-btn
              color="primary"
              :disabled="!isReady"
              :loading="processing"
              class="full-width text-bold horizontal-gradient"
              size="medium"
              no-caps
              unelevated
              @click="subscribe"
            >
              <q-spinner v-if="processing" size="24px" color="white" />
              <span v-else-if="trialDays">Start {{ trialDays }}-Day Trial</span>
              <span v-else-if="addOn">Activate {{ planName }}</span>
              <span v-else>Subscribe</span>
            </q-btn>
            <div class="text-grey-7 q-mt-sm text-center">
              Cancel anytime with just a few clicks from your dashboard.
            </div>
          </div>
          <div v-else>
            Please reach out to your primary account owner,
            <b>{{
              organizationMembers.find((_) => _.id === organization?.owner_user_id)?.email ||
              'Unknown'
            }}</b
            >, to make this purchase or
            <a href="#" @click="openIntercom" class="text-primary">contact us</a> to help!
          </div>
        </q-card-section>
      </q-card>
    </div>
    <div class="col-12 col-md-4 q-gutter-y-md">
      <q-card bordered flat>
        <q-card-section class="q-pa-lg">
          <div class="text-weight-medium q-mb-md" style="font-size: 1.1rem">Order Summary</div>
          <slot></slot>
          <q-separator class="q-my-sm" />
          <div class="row text-weight-medium justify-between">
            <div class="col">Subtotal</div>
            <div class="col text-right">{{ promoFormattedPrice || formatMoney(price, 2) }}</div>
          </div>
          <div v-if="selectedCouponText" class="row q-mt-sm">
            <div class="col">Discount</div>
            <div class="col text-right">{{ selectedCouponText }}</div>
          </div>
          <q-separator class="q-my-sm" />
          <div class="row text-subtitle1 text-weight-medium">
            <div class="col">Total due today</div>
            <div v-if="trialDays" class="col text-green text-right text-bold">$0.00</div>
            <div v-else class="col text-green text-right text-bold">
              {{ promoFormattedPrice || dueAfterPromo }}
            </div>
          </div>
          <div
            v-if="trialDays"
            class="q-pt-sm"
            :class="[$q.dark.isActive ? 'text-grey-5' : 'text-grey-7']"
          >
            After your trial ends, you will be charged {{ dueAfterPromo }} (+ applicable taxes) per
            month starting {{ trialEndDate }}. You can always cancel before then.
          </div>
          <div v-if="addOn" class="text-grey-7 q-pt-sm">
            This add-on will be added to your existing Property Reports subscription. A prorated
            charge will be applied immediately, followed by the full amount on your next billing
            date.
          </div>
        </q-card-section>
      </q-card>
    </div>
    <div v-if="!addOn" class="col-12 col-md-12 q-gutter-y-md">
      <div class="q-mt-xl">
        <div class="text-h6 text-primary q-mb-md">Included in your Professional Plan</div>
        <div class="row q-col-gutter-md q-mt-md items-stretch">
          <div
            v-for="feature in features.filter((f) => !f.hidden)"
            :key="feature.title"
            class="col-12 col-md-6 col-lg-4"
          >
            <q-card flat bordered class="card-container">
              <q-card-section class="q-pa-md">
                <div class="flex">
                  <div class="q-mt-xs">
                    <q-icon :name="feature.icon" color="primary" size="21px" class="q-mr-md" />
                  </div>
                  <div class="col">
                    <div class="text-subtitle1 text-weight-medium">{{ feature.title }}</div>
                    <div
                      :class="[$q.dark.isActive ? 'text-grey-5' : 'text-grey-7']"
                      style="min-height: 60px"
                    >
                      {{ feature.description }}
                    </div>
                  </div>
                </div>
              </q-card-section>
            </q-card>
          </div>
        </div>
      </div>

      <q-expansion-item class="q-mt-md" switch-toggle-side header-class="text-primary">
        <template #header>
          <div class="text-primary text-weight-medium">See all 16 premium features</div>
        </template>

        <div class="row q-col-gutter-md q-mt-md">
          <div
            v-for="feature in features.filter((f) => f.hidden)"
            :key="feature.title"
            class="col-12 col-md-6 col-lg-4"
          >
            <q-card flat bordered class="card-container">
              <q-card-section class="q-pa-md">
                <div class="row">
                  <div class="q-mt-xs">
                    <q-icon :name="feature.icon" color="primary" size="21px" class="q-mr-md" />
                  </div>
                  <div class="col">
                    <div class="text-subtitle1 text-weight-medium">{{ feature.title }}</div>
                    <div
                      :class="[$q.dark.isActive ? 'text-grey-5' : 'text-grey-7']"
                      style="min-height: 60px"
                    >
                      {{ feature.description }}
                    </div>
                  </div>
                </div>
              </q-card-section>
            </q-card>
          </div>
        </div>
      </q-expansion-item>
    </div>
  </div>
</template>

<script lang="ts">
import stripeAPI from '@/api/stripe'
import { formatMoney } from '@/utils'
import { format, addDays } from 'date-fns'
import {
  loadStripe,
  type Stripe,
  type StripeElements,
  type StripeCardElement,
  type StripeAddressElement
} from '@stripe/stripe-js'
import type { BillingAddress, OrganizationAttributes } from 'common'
import { useUserStore } from '@/stores/user'

declare const Intercom: (action: string, ...args: any[]) => void

export default {
  name: 'CheckoutCard',
  props: {
    planName: {
      type: String,
      required: true
    },
    price: {
      type: Number,
      required: true
    },
    promoFormattedPrice: {
      type: String,
      default: ''
    },
    trialDays: {
      type: Number,
      default: 0
    },
    addOn: {
      type: Boolean,
      default: false
    }
  },
  emits: ['create-subscription'],
  data() {
    return {
      stripe: null as Stripe | null,
      formatMoney,
      promoCodeError: false,
      agreeToTerms: false,
      cardHolderName: '',
      address: {
        line1: '',
        line2: undefined,
        city: '',
        state: '',
        postal_code: '',
        country: 'US'
      } as BillingAddress,
      cardElements: null as null | StripeElements,
      cardElementInputs: [] as (StripeCardElement | StripeAddressElement)[],
      processing: false,
      promoCode: '',
      promoCodeTimeout: undefined as undefined | ReturnType<typeof setTimeout>,
      selectedCoupon: null as null | {
        amount_off: number | null
        percent_off: number | null
        id: string
        active: boolean
      },
      isCardValid: false,
      isAddressValid: false,
      features: [
        {
          icon: 'all_inclusive',
          title: 'Customized Reports',
          description:
            'Generate 10 in-depth property reports, providing full visibility into rents, concessions, amenities, and market trends for every community you analyze.',
          hidden: false
        },
        {
          icon: 'update',
          title: 'Daily Updates',
          description:
            'Stay ahead with daily-refreshed data on 35M+ multifamily units, ensuring your analyses always reflect the most current pricing and market shifts.',
          hidden: false
        },
        {
          icon: 'discount',
          title: 'Concession Detection',
          description:
            'Automatically extract and analyze concessions like free months, discounts, and waived fees from property listings using AI-powered tools.',
          hidden: false
        },
        {
          icon: 'rocket_launch',
          title: 'New Development',
          description:
            'Assess feasibility for new multifamily projects by identifying optimal unit mixes, amenities, and market rents with minimal input.',
          hidden: false
        },
        {
          icon: 'tune',
          title: 'Comps Customization',
          description:
            'Customize comparable property analyses by adding or removing comps and adjusting filters to match specific criteria.',
          hidden: false
        },
        {
          icon: 'stars',
          title: 'Comps Recommendation',
          description:
            'Leverage AI to identify the most statistically similar rent comps based on factors like location, property type, and amenities.',
          hidden: false
        },
        {
          icon: 'table_chart',
          title: 'Excel Export',
          description:
            'Export detailed, unit-level data into Excel, complete with charts and pivot tables for in-depth analysis.',
          hidden: false
        },
        {
          icon: 'picture_as_pdf',
          title: 'PDF Export',
          description:
            'Generate stylized PDF reports of market surveys, including dynamic charts and visuals, for easy sharing with stakeholders.',
          hidden: false
        },
        {
          icon: 'attach_money',
          title: 'Price Recommendation',
          description:
            'Receive daily, data-driven rent pricing suggestions for each unit, informed by real-time market conditions and comparable listings.',
          hidden: true
        },
        {
          icon: 'share',
          title: 'Report Sharing',
          description:
            'Easily share property reports with team members by generating shareable links, facilitating seamless collaboration.',
          hidden: true
        },
        {
          icon: 'lock',
          title: 'Report Access Customization',
          description:
            'Control who can view or edit reports by setting visibility options to private, public, or organization-wide access.',
          hidden: true
        },
        {
          icon: 'bar_chart',
          title: 'Expense Benchmark Customization',
          description:
            'Compare operating expenses against similar properties using benchmarks based on factors like year built, unit count, and location.',
          hidden: true
        },
        {
          icon: 'rate_review',
          title: 'Online Review Analysis',
          description:
            'Analyze public apartment reviews to identify positive and negative sentiments across categories such as management, location, and amenities.',
          hidden: true
        },
        {
          icon: 'insights',
          title: 'Pricing Strategy Analysis',
          description:
            "Evaluate competitors' pricing strategies by examining factors like price stability, frequency of changes, and time on market.",
          hidden: true
        },
        {
          icon: 'image_search',
          title: 'Picture Analysis',
          description:
            'Utilize computer vision to assess property quality and amenities from photos, assigning objective quality scores to aid in valuations.',
          hidden: true
        },
        {
          icon: 'dashboard',
          title: 'Intuitive Interface',
          description:
            'Navigate a user-friendly dashboard designed for efficiency, enabling quick access to key data and tools for market analysis.',
          hidden: true
        },
        {
          icon: 'filter_alt',
          title: 'Query Builder',
          description:
            'Create custom datasets by selecting variables, applying filters, and scheduling regular data deliveries for comprehensive analysis.',
          hidden: true
        },
        {
          icon: 'public',
          title: 'Market Analytics',
          description:
            'Access market analytics for specific regions, tracking trends over time to inform investment decisions and strategy.',
          hidden: true
        }
      ]
    }
  },
  computed: {
    stripeVariables() {
      const isDark = this.$q.dark.isActive
      return {
        colorText: isDark ? 'white' : 'black',
        colorBackground: isDark ? '#212121' : undefined,
        borderRadius: '4px',
        focusBoxShadow: 'none',
        fontSizeBase: '1em',
        colorPrimary: '#6365f1'
      }
    },
    isPrimaryOwner() {
      // is the actual owner of the organization in the DB
      return this.$auth0.user.value?.sub === this.organization?.owner_user_id
    },
    organization(): OrganizationAttributes | null {
      return useUserStore().organization
    },
    organizationMembers() {
      return useUserStore().organizationMembers
    },
    trialEndDate() {
      const endDay = addDays(new Date(), this.trialDays)
      return format(endDay, 'MMMM d, y')
    },
    dueAfterPromo() {
      let dueToday = this.price
      const percentOff = (this.selectedCoupon?.percent_off || 0) / 100
      const amountOff = (this.selectedCoupon?.amount_off || 0) / 100
      if (percentOff) {
        dueToday = dueToday * (1 - percentOff)
      }
      if (amountOff) {
        dueToday = dueToday - amountOff
      }
      return formatMoney(dueToday, 2)
    },
    selectedCouponText() {
      const percentOff = this.selectedCoupon?.percent_off
      const amountOff = (this.selectedCoupon?.amount_off || 0) / 100

      if (percentOff) {
        return `${percentOff}% off`
      } else if (amountOff) {
        return `${formatMoney(amountOff)} off`
      }
      return ''
    },
    isReady() {
      return (
        (this.addOn && this.agreeToTerms) ||
        (this.isCardValid && this.isAddressValid && this.agreeToTerms)
      )
    }
  },
  watch: {
    promoCode() {
      clearTimeout(this.promoCodeTimeout)
      this.promoCodeTimeout = setTimeout(() => {
        this.checkPromoCode()
      }, 500)
    },
    '$q.dark.isActive'() {
      this.cardElements?.update({ appearance: { variables: this.stripeVariables } })
      this.cardElementInputs.forEach((el) => {
        el.update({ style: { base: { color: this.stripeVariables.colorText } } })
      })
    }
  },
  async mounted() {
    const stripe = await this.getStripe()
    if (stripe && !this.addOn) {
      const elements = stripe.elements({
        appearance: { variables: this.stripeVariables }
      })
      const cardNumberElement = elements.create('card', {
        hidePostalCode: true,
        style: { base: { color: this.stripeVariables.colorText } }
      })
      cardNumberElement.on('change', (event) => {
        this.isCardValid = event.complete
        const displayError = document.getElementById('card-errors')
        if (displayError) {
          if (event.error) {
            displayError.textContent = event.error.message
          } else {
            displayError.textContent = ''
          }
        }
      })
      cardNumberElement.mount('#card-element')
      const addressElement = elements.create('address', {
        mode: 'billing',
        autocomplete: {
          mode: 'google_maps_api',
          apiKey: import.meta.env.VITE_GOOGLE_API_KEY
        },
        allowedCountries: ['US']
      })
      addressElement.on('change', (event) => {
        this.isAddressValid = event.complete
        this.address.line1 = event.value.address.line1
        this.address.line2 = event.value.address.line2 || undefined
        this.address.city = event.value.address.city
        this.address.state = event.value.address.state
        this.address.postal_code = event.value.address.postal_code
        this.cardHolderName = event.value.name
      })
      addressElement.mount('#address-element')
      this.cardElementInputs.push(cardNumberElement, addressElement)
      this.cardElements = elements
    }
  },
  methods: {
    openIntercom() {
      this.$tracker.openedIntercom()
      Intercom('show')
    },
    async getStripe(): Promise<Stripe> {
      if (!this.stripe) {
        this.stripe = await loadStripe(import.meta.env.VITE_STRIPE_PRODUCT_ID).catch(() => {
          return null
        })
      }
      if (!this.stripe) {
        return await this.getStripe()
      }
      return this.stripe
    },
    async checkPromoCode() {
      if (!this.promoCode) {
        this.promoCodeError = false
        this.selectedCoupon = null
        return
      }
      const data = await stripeAPI.checkPromoCode(this.promoCode)
      if (data && data.active) {
        this.selectedCoupon = data
        this.promoCodeError = false
      } else {
        this.promoCodeError = true
        this.selectedCoupon = null
      }
    },
    async subscribe() {
      let paymentMethodId = null
      this.processing = true
      if (!this.addOn) {
        const stripe = await this.getStripe()
        if (!this.cardElements) {
          this.processing = false
          return
        }
        const cardElements = this.cardElements.getElement('card')
        if (!cardElements) {
          this.processing = false
          return
        }
        const { paymentMethod, error } = await stripe.createPaymentMethod({
          type: 'card',
          billing_details: {
            name: this.cardHolderName,
            address: {
              postal_code: this.address.postal_code
            }
          },
          card: cardElements
        })
        if (!paymentMethod || error) {
          this.processing = false
          return
        }
        if (this.selectedCoupon) {
          this.$tracker.usedCoupon(this.selectedCoupon.id)
        }
        paymentMethodId = paymentMethod.id
      }
      this.$emit('create-subscription', {
        paymentId: paymentMethodId,
        couponId: this.selectedCoupon?.id,
        address: this.address,
        cb: async () => {
          let path = ''
          if (this.$route.name === 'Subscribe to Property Reports') {
            path = '/property-reports'
          } else if (this.$route.name === 'Subscribe to Market Analytics') {
            path = '/market-analytics'
          }
          await this.$router.push({ name: 'Thank You', query: { path } })
          this.processing = false
        },
        failCb: () => {
          const displayError = document.getElementById('card-errors')
          if (displayError) {
            displayError.textContent =
              'An error occurred. Please confirm payment details then try again.'
          }
          this.processing = false
        }
      })
    }
  }
}
</script>
<style scoped lang="scss">
#card-form {
  display: flex;
  justify-content: space-around;
}

#card-element {
  border-radius: 4px;
  padding: 0.8rem;
  border: 1px solid rgba(50, 50, 93, 0.1);
  box-shadow:
    0px 1px 1px rgba(0, 0, 0, 0.03),
    0px 3px 6px rgba(0, 0, 0, 0.02);
}

.body--dark {
  #card-element {
    border: 1px solid #424242;
  }
}

.horizontal-gradient {
  background: linear-gradient(to right, #6366f1, #9333ea) !important;
}

.card-container {
  box-shadow: 0 4px 10px rgba(120, 120, 120, 0.1) !important;
}
</style>
